import * as animate from '@/js/util/animate'

class Accordion {
  heading = ''
  #container
  #elements
  #ids
  #currentIndex
  #initialized = false
  #useHistory = true

  #headerSelector = '.accordion-heading'
  #contentSelector = '.accordion-inner'

  #getCurrentElement() {
    return this.#elements[this.#currentIndex]
  }

  #getCurrentIndexFromHash() {
    let hashId = location.hash.replace(/^#/, '')
    if (hashId.length === 0) {
      return undefined
    }

    if (this.#ids.includes(hashId)) return this.#ids.indexOf(hashId)

    let containedElement = document.getElementById(hashId)

    let index = 0
    for (let element of this.#elements) {
      if (element.contains(containedElement)) {
        return index
      }
      index++
    }

    return undefined
  }

  #createEvent() {
    return new CustomEvent('accordion:toggled', {
      bubbles: true,
    })
  }

  constructor(container) {
    this.#container = container
    this.#useHistory = container.dataset.disableHistory === undefined
    this.#elements = [...container.children]
    this.#elements.forEach((element, index) => {
      element.querySelector(this.#contentSelector).style.display = 'none'
      let headerElement = element.querySelector(this.#headerSelector)
      headerElement.addEventListener('click', event => {
        this.toggle(index)
      })
      headerElement.addEventListener('keydown', event => {
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          let newIndex = (index - 1 + this.#elements.length) % this.#elements.length
          this.#elements[newIndex].querySelector(this.#headerSelector).focus()
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault()
          let newIndex = (index + 1) % this.#elements.length
          this.#elements[newIndex].querySelector(this.#headerSelector).focus()
        }

        if (event.key === 'Home') {
          event.preventDefault()
          let newIndex = 0
          this.#elements[newIndex].querySelector(this.#headerSelector).focus()
        }

        if (event.key === 'End') {
          event.preventDefault()
          let newIndex = this.#elements.length - 1
          this.#elements[newIndex].querySelector(this.#headerSelector).focus()
        }
      })
      headerElement.setAttribute('aria-expanded', 'false')
    })

    this.#ids = [...container.children].map(el => el.id)

    let index = this.#getCurrentIndexFromHash()

    if (typeof index === 'number') {
      this.#elements[index].classList.add('accordion-open')
      this.#elements[index]
        .querySelector(this.#headerSelector)
        .setAttribute('aria-expanded', 'true')
      this.#elements[index].querySelector(this.#contentSelector).style.display = 'block'

      this.#currentIndex = index
    }

    this.#initialized = true

    if (typeof index === 'number') {
      this.#elements[index].dispatchEvent(this.#createEvent())
    }

    if (this.#useHistory) {
      window.addEventListener('popstate', () => {
        let index = this.#getCurrentIndexFromHash()

        if (typeof index === 'number') {
          this.open(index, { push: false })
        } else if (typeof this.#currentIndex === 'number') {
          this.close(this.#currentIndex, { push: false })
        }
      })
    }
  }

  open(index, options) {
    this.toggle(index, true, options)
  }

  close(index, options) {
    this.toggle(index, false, options)
  }

  toggle(index, state, { push = true } = {}) {
    let targetElement = this.#elements[index]
    if (!targetElement) return

    let currentState = index === this.#currentIndex
    let newState = typeof state === 'boolean' ? state : !currentState

    if (newState === currentState) return

    let targetContentElement = targetElement.querySelector(this.#contentSelector)
    let targetHeaderElement = targetElement.querySelector(this.#headerSelector)

    let scrollTarget
    if (newState) {
      targetElement.classList.add('accordion-open')
      targetHeaderElement.setAttribute('aria-expanded', 'true')

      if (typeof this.#currentIndex === 'number') {
        let currentElement = this.#getCurrentElement()
        let currentContentElement = currentElement.querySelector(this.#contentSelector)
        let currentHeaderElement = currentElement.querySelector(this.#headerSelector)

        currentElement.classList.remove('accordion-open')
        currentHeaderElement.setAttribute('aria-expanded', 'false')

        // Calculate scroll position of the new open accordion after closing the current one
        // This allows scrolling to the top of the new accordion if needed
        currentContentElement.style.display = 'none'
        let upperEdge = targetHeaderElement.getBoundingClientRect().top
        if (upperEdge < 0) {
          scrollTarget =
            window.scrollY +
            upperEdge -
            parseFloat(getComputedStyle(targetHeaderElement).scrollMarginTop)
        }
        currentContentElement.style.display = 'block'

        animate.slideUp(currentContentElement).then(() => {
          currentElement.dispatchEvent(this.#createEvent())
        })
      }

      animate.slideDown(targetContentElement).then(() => {
        targetElement.dispatchEvent(this.#createEvent())
      })

      if (typeof scrollTarget === 'number') {
        window.scrollTo({
          top: scrollTarget,
          behavior: 'smooth',
        })
      }

      if (this.#initialized) {
        if (push && this.#useHistory) {
          window.history.pushState(
            null,
            null,
            window.location.pathname +
              window.location.search +
              (targetElement.id ? `#${targetElement.id}` : ''),
          )
        }
      }

      this.#currentIndex = index
    } else {
      targetElement.classList.remove('accordion-open')
      targetHeaderElement.setAttribute('aria-expanded', 'false')

      animate.slideUp(targetContentElement).then(() => {
        targetElement.dispatchEvent(this.#createEvent())
      })

      if (push && this.#useHistory) {
        window.history.pushState(null, null, window.location.pathname + window.location.search)
      }

      this.#currentIndex = undefined
    }
  }
}

for (let element of document.querySelectorAll(
  '.wpce-accordion:first-child, *:not(.wpce-accordion) + .wpce-accordion',
)) {
  let group = [element]

  let current = element
  while (current.nextElementSibling?.classList.contains('wpce-accordion')) {
    current = current.nextElementSibling
    group.push(current)
  }

  let disableHistory = false
  let elementsWithoutHistory = group.filter(element => element.dataset.disableHistory !== undefined)
  if (elementsWithoutHistory.length > 0) {
    if (elementsWithoutHistory.length < group.length) {
      console.warn(
        'Accordion groups should not have a mix of elements with and without the `data-disable-history` attribute.',
      )
    } else {
      disableHistory = true
    }
  }

  let accordionContainer = document.createElement('div')
  accordionContainer.classList.add('accordion-group')
  accordionContainer.dataset.disableHistory = disableHistory ? '' : undefined
  element.parentNode.insertBefore(accordionContainer, element)

  for (let groupElement of group) {
    accordionContainer.appendChild(groupElement)
  }

  new Accordion(accordionContainer)
}
